import { useState, useEffect } from 'react';
import { Badge, Icon } from 'components/ui';
import { exportData } from 'lib/exporter';
import { fetchContentLibrary } from 'lib/supabase-db';
import { ContentLibrary as ContentLibraryType } from 'types';
import ContentPreview from 'components/ContentPreview';
import Modal from 'components/Modal';
import ResponseRenderer from 'components/renderers/ResponseRenderer';
import CopyButton from 'components/renderers/CopyButton';

const CONTENT_TYPE_LABELS: Record<string, string> = {
  linkedin_agent: 'LinkedIn Post',
  twitter_agent: 'Twitter Post',
  newsletter_agent: 'Newsletter',
  press_release_agent: 'Press Release',
  marketing_strategist_agent: 'Marketing Strategy',
  marketing_landing_page: 'Landing Page',
  marketing_blog: 'Blog Post',
  marketing_strategy: 'Branding & Positioning',
  seo_keyword_agent: 'SEO Keywords',
  seo_content_agent: 'SEO Content',
};

const getContentTypeIcon = (contentType: string): any => {
  const iconMap: Record<string, string> = {
    linkedin_agent: 'linkedin',
    twitter_agent: 'share',
    newsletter_agent: 'write',
    press_release_agent: 'share',
    marketing_strategist_agent: 'structure',
    marketing_landing_page: 'structure',
    marketing_blog: 'write',
    marketing_strategy: 'structure',
    seo_keyword_agent: 'search',
    seo_content_agent: 'search',
  };
  return iconMap[contentType] || 'list';
};

// Get display label for marketing_strategist_agent based on page_type
const getMarketingDisplayType = (item: ContentLibraryType): string => {
  if (item.content_type !== 'marketing_strategist_agent') {
    return item.content_type;
  }

  const pageType = item.content?.agent_output?.page_type;

  // Map page_type to display type
  if (['landing_page', 'product', 'persona', 'competitor', 'integration'].includes(pageType)) {
    return 'marketing_landing_page';
  }
  if (pageType === 'blog_post') {
    return 'marketing_blog';
  }
  if (['positioning_document', 'website_structure'].includes(pageType)) {
    return 'marketing_strategy';
  }

  // Fallback to generic marketing
  return 'marketing_strategist_agent';
};

// Get a meaningful title from the content
const getContentTitle = (item: ContentLibraryType): string => {
  const data = item.content?.agent_output;
  if (!data) return item.title;

  // For marketing strategist - get title from hero.h1 or meta_title
  if (item.content_type === 'marketing_strategist_agent') {
    const title = data.hero?.h1 || data.meta_title || data.positioning_statement || '';
    if (title) {
      // Truncate if too long
      return title.length > 40 ? `${title.substring(0, 40).trim()}...` : title;
    }
  }

  // For other agents
  if (item.content_type === 'linkedin_agent') {
    const content = data.posts?.[0]?.content || data.posts?.[0]?.post || '';
    return content.length > 40 ? `${content.substring(0, 40).trim()}...` : content || item.title;
  }

  if (item.content_type === 'twitter_agent') {
    const content = data.tweets?.[0]?.content || '';
    return content.length > 40 ? `${content.substring(0, 40).trim()}...` : content || item.title;
  }

  if (item.content_type === 'newsletter_agent') {
    return data.title || item.title;
  }

  if (item.content_type === 'press_release_agent') {
    return data.headline || item.title;
  }

  if (item.content_type === 'seo_keyword_agent') {
    const keywords = data.suggested_keywords
      ?.slice(0, 3)
      .map((k: any) => k.keyword)
      .join(', ');
    return keywords ? `Keywords: ${keywords}` : item.title;
  }

  if (item.content_type === 'seo_content_agent') {
    return data.meta_description?.substring(0, 40) + '...' || item.title;
  }

  return item.title;
};

export const ContentLibrary = () => {
  const [contentItems, setContentItems] = useState<ContentLibraryType[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [modalItem, setModalItem] = useState<ContentLibraryType | null>(null);

  useEffect(() => {
    const loadContent = async () => {
      try {
        setIsLoading(true);
        const data = await fetchContentLibrary(selectedType);
        setContentItems(data);
      } catch (error) {
        console.error('Error fetching content:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType]);

  const filteredContent = contentItems.filter((item) => {
    if (!searchQuery) return true;

    const searchLower = searchQuery.toLowerCase();

    // Search in title
    if (item.title.toLowerCase().includes(searchLower)) {
      return true;
    }

    // Search in full content JSON
    try {
      const contentString = JSON.stringify(item.content).toLowerCase();
      return contentString.includes(searchLower);
    } catch (error) {
      // Fallback if JSON.stringify fails
      const contentString = String(item.content).toLowerCase();
      return contentString.includes(searchLower);
    }
  });

  return (
    <div className="h-screen flex flex-col">
      {/* Header - Fixed */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200">
        <div className="p-6">
          <div className="my-4 py-6 text-center">
            <h1 className="text-5xl font-medium text-gray-900 mb-4">Content Library</h1>
            <p className="text-sm">
              All your generated posts and AI outputs, saved automatically for the future use.
            </p>
          </div>

          {/* Search and Filter */}
          <div className="flex w-5/6 mx-auto gap-4">
            <div className="flex-1 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <Icon type="searchLight" className="w-4" />
              </span>
              <input
                type="text"
                placeholder="Search content..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 placeholder:text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="w-full mt-4 flex justify-evenly gap-4">
            <button
              onClick={() => setSelectedType('all')}
              className={`cursor-pointer ${
                selectedType === 'all' ? 'text-black underline' : 'font-bold text-gray-600/50'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setSelectedType('linkedin_agent')}
              className={`cursor-pointer ${
                selectedType === 'linkedin_agent'
                  ? 'text-black underline'
                  : 'text-gray-600/50 font-bold'
              }`}
            >
              LinkedIn
            </button>
            <button
              onClick={() => setSelectedType('twitter_agent')}
              className={`cursor-pointer ${
                selectedType === 'twitter_agent'
                  ? 'text-black underline'
                  : 'text-gray-600/50 font-bold'
              }`}
            >
              Twitter
            </button>
            <button
              onClick={() => setSelectedType('newsletter_agent')}
              className={`cursor-pointer ${
                selectedType === 'newsletter_agent'
                  ? 'text-black underline'
                  : 'text-gray-600/50 font-bold'
              }`}
            >
              Newsletter
            </button>
            <button
              onClick={() => setSelectedType('press_release_agent')}
              className={`cursor-pointer ${
                selectedType === 'press_release_agent'
                  ? 'text-black underline'
                  : 'text-gray-600/50 font-bold'
              }`}
            >
              Press Release
            </button>
            <button
              onClick={() => setSelectedType('marketing_landing_page')}
              className={`cursor-pointer ${
                selectedType === 'marketing_landing_page'
                  ? 'text-black underline'
                  : 'text-gray-600/50 font-bold'
              }`}
            >
              Landing Pages
            </button>
            <button
              onClick={() => setSelectedType('marketing_blog')}
              className={`cursor-pointer ${
                selectedType === 'marketing_blog'
                  ? 'text-black underline'
                  : 'text-gray-600/50 font-bold'
              }`}
            >
              Blogs
            </button>
            <button
              onClick={() => setSelectedType('marketing_strategy')}
              className={`cursor-pointer ${
                selectedType === 'marketing_strategy'
                  ? 'text-black underline'
                  : 'text-gray-600/50 font-bold'
              }`}
            >
              Branding & Positioning
            </button>
            <button
              onClick={() => setSelectedType('seo_keyword_agent')}
              className={`cursor-pointer ${
                selectedType === 'seo_keyword_agent'
                  ? 'text-black underline'
                  : 'text-gray-600/50 font-bold'
              }`}
            >
              SEO Keywords
            </button>
          </div>
        </div>
      </div>

      {/* Content Grid - Scrollable only this area */}
      <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Loading content...</p>
          </div>
        ) : filteredContent.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">No content found</p>
            {(searchQuery || selectedType !== 'all') && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedType('all');
                }}
                className="text-purple-600 hover:text-purple-700 font-medium"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div className="grid gap-4 max-w-5xl mx-auto">
            {filteredContent.map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-lg border border-gray-200 p-4 cursor-pointer hover:border-purple-300 hover:shadow-md transition-all duration-200"
                onClick={() => setModalItem(item)}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex flex-row items-center gap-3 mb-1">
                      <Icon
                        type={getContentTypeIcon(getMarketingDisplayType(item))}
                        className="w-4"
                      />
                      <h3 className="font-semibold self-center text-gray-900 mb-0 text-sm">
                        {CONTENT_TYPE_LABELS[getMarketingDisplayType(item)] || 'Content'} -{' '}
                        {getContentTitle(item)}
                      </h3>
                    </div>
                    <p className="text-xs text-gray-500">
                      {new Date(item.created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </p>
                  </div>
                </div>

                {/* Content Preview */}
                <ContentPreview contentType={item.content_type} content={item.content} />

                {/* Export buttons */}
                <div
                  className="flex gap-2 mt-2 pt-2 border-t border-gray-200"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() =>
                      exportData({
                        agentType: item.content_type,
                        content: item.content?.agent_output || {},
                        title: item.title,
                        createdAt: item.created_at,
                        exportType: 'html',
                      })
                    }
                    className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200 flex items-center gap-1"
                  >
                    <Icon type="share" className="w-3" />
                    HTML
                  </button>
                  <button
                    onClick={() =>
                      exportData({
                        agentType: item.content_type,
                        content: item.content?.agent_output || {},
                        title: item.title,
                        createdAt: item.created_at,
                        exportType: 'md',
                      })
                    }
                    className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 flex items-center gap-1"
                  >
                    <Icon type="share" className="w-3" />
                    MD
                  </button>
                  {item.content_type === 'seo_keyword_agent' && (
                    <button
                      onClick={() =>
                        exportData({
                          agentType: item.content_type,
                          content: item.content?.agent_output || {},
                          title: item.title,
                          createdAt: item.created_at,
                          exportType: 'csv',
                        })
                      }
                      className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 flex items-center gap-1"
                    >
                      <Icon type="share" className="w-3" />
                      CSV
                    </button>
                  )}
                </div>

                {/* View in Conversation link */}
                {item.conversation_id && (item.tags?.length ?? 0) > 0 && (
                  <div className="flex flex-row gap-4 items-center mt-3 pt-3 border-t border-gray-200">
                    <span className="text-xs">Tags</span>
                    <div className="flex gap-2 text-xs">
                      {item.tags?.map((tag: string, i: number) => (
                        <Badge
                          className="text-xs bg-link/10 py-0.5 border-link"
                          key={`${tag}-${i}`}
                          variant="outline"
                        >
                          #{tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Content Details Modal */}
      <Modal
        isOpen={modalItem !== null}
        onClose={() => setModalItem(null)}
        size="lg"
        title={`${
          modalItem
            ? CONTENT_TYPE_LABELS[getMarketingDisplayType(modalItem)] || 'Content'
            : 'Content'
        } - ${modalItem ? getContentTitle(modalItem) : 'Details'}`}
      >
        {modalItem && (
          <div className="max-h-[70vh] flex flex-col">
            {/* Metadata */}
            <div className="text-sm text-gray-500 border-b border-gray-200 pb-3 mb-4">
              <p>
                Created:{' '}
                {new Date(modalItem.created_at).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </p>
              {modalItem.tags && modalItem.tags.length > 0 && (
                <p className="mt-1">Tags: {modalItem.tags.map((tag) => `#${tag}`).join(' ')}</p>
              )}
            </div>

            {/* Full Content Renderer */}
            <div className="flex-1 overflow-y-auto min-h-0">
              <ResponseRenderer
                agentType={modalItem.content_type}
                data={modalItem.content?.agent_output || modalItem.content}
              />
            </div>

            {/* Export Actions */}
            <div className="flex gap-2 pt-3 border-t border-gray-200 mt-4">
              <button
                onClick={() =>
                  exportData({
                    agentType: modalItem.content_type,
                    content: modalItem.content?.agent_output || {},
                    title: modalItem.title,
                    createdAt: modalItem.created_at,
                    exportType: 'html',
                  })
                }
                className="text-sm px-3 py-2 bg-purple-100 text-purple-700 rounded hover:bg-purple-200 flex items-center gap-2"
              >
                <Icon type="share" className="w-4" />
                HTML
              </button>
              <button
                onClick={() =>
                  exportData({
                    agentType: modalItem.content_type,
                    content: modalItem.content?.agent_output || {},
                    title: modalItem.title,
                    createdAt: modalItem.created_at,
                    exportType: 'md',
                  })
                }
                className="text-sm px-3 py-2 bg-green-100 text-green-700 rounded hover:bg-green-200 flex items-center gap-2"
              >
                <Icon type="share" className="w-4" />
                Markdown
              </button>
              {modalItem.content_type === 'seo_keyword_agent' && (
                <button
                  onClick={() =>
                    exportData({
                      agentType: modalItem.content_type,
                      content: modalItem.content?.agent_output || {},
                      title: modalItem.title,
                      createdAt: modalItem.created_at,
                      exportType: 'csv',
                    })
                  }
                  className="text-sm px-3 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 flex items-center gap-2"
                >
                  <Icon type="share" className="w-4" />
                  CSV
                </button>
              )}
              <div className="relative">
                <CopyButton
                  agentType={modalItem.content_type}
                  agentOutput={modalItem.content?.agent_output || modalItem.content}
                />
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};
